<script setup>
import { ref, computed } from 'vue'
import JSConfetti from 'js-confetti'
import Modal from '../components/Modal.vue'
import ShareButtons from '../components/ShareButtons.vue'
import { api } from '../composables/useApi.js'
import { useTimer } from '../composables/useTimer.js'
import { useTiers, getTierTextClass } from '../composables/useTiers.js'
import { useButtonEvent } from '../composables/useButtonEvent.js'
import {
    formatSecondsToFullTime,
    formatDuration,
} from '../composables/useFormatting.js'

const props = defineProps({
    endpoint: { type: String, required: true },
    seconds: { type: Number, required: true },
    tiers: { type: Array, default: () => [] },
    totalPresses: { type: Number, default: 0 },
    isAuthenticated: { type: Boolean, default: false },
    hasPendingPress: { type: Boolean, default: false },
})

const pressedModal = ref(null)
const count = ref(props.totalPresses)
const message = ref('')
const hasPendingPress = ref(props.hasPendingPress)
const canPress = computed(() => props.isAuthenticated || !hasPendingPress.value)

const lastPress = ref({
    tier_label: '',
    elapsed_seconds: 0,
    owner: null,
})

// Composables
const { timer, syncWithServer, resetTimer } = useTimer(props.seconds)
const tiersRef = computed(() => props.tiers)
const { currentTierValue, currentTierLabel } = useTiers(tiersRef, timer)

// Computed
const formattedTime = computed(() => formatSecondsToFullTime(timer.value))

const shareUrl = computed(() => {
    if (lastPress.value.owner) {
        return `${window.location.origin}/user/${lastPress.value.owner}/`
    }
    return window.location.origin
})

const shareText = computed(() => {
    return `I pressed the button at ${formatDuration(lastPress.value.elapsed_seconds)} and achieved the ${lastPress.value.tier_label} tier! #thebutton`
})

// Event handlers
const handleReset = (e) => {
    if (e.detail?.total_count) {
        count.value = e.detail.total_count
    }
    resetTimer()
}

useButtonEvent(handleReset)

/**
 * Sends a button press to the server.
 */
const sendPress = () => {
    if (!canPress.value) return

    message.value = ''

    api.post(props.endpoint)
        .then((response) => {
            if (response.status === 201) {
                syncWithServer(response.data.seconds_since)

                lastPress.value = response.data

                if (!props.isAuthenticated) {
                    hasPendingPress.value = true
                }

                pressedModal.value.show()

                const jsConfetti = new JSConfetti()
                jsConfetti.addConfetti({
                    confettiNumber: window.innerWidth < 768 ? 40 : 100,
                })
            }
        })
        .catch((error) => {
            if (error.response?.status === 429) {
                message.value =
                    error.response.data.detail ||
                    'Too many requests. Please wait before pressing again.'
            } else if (
                error.response?.status === 403 &&
                // keep in sync with thebutton/constants.py DUPLICATE_PRESS_CODE
                error.response.data?.code === 'duplicate_anonymous_press'
            ) {
                hasPendingPress.value = true
            } else {
                console.error('Error registering button press:', error)
            }
        })
}
</script>

<template>
    <div class="c-timer">
        <div class="c-timer__display">
            <span class="c-timer__label">Time since last press</span>
            <span class="c-timer__time">
                {{ formattedTime }}
            </span>
        </div>

        <div class="c-timer__info">
            <div class="c-timer__rank-badge">
                <span class="c-timer__rank-label">Current Rank</span>
                <span
                    class="c-timer__rank-value"
                    :class="getTierTextClass(currentTierValue)"
                >
                    {{ currentTierLabel }}
                </span>
            </div>

            <div class="c-timer__presses">
                Total presses:
                <span class="c-timer__presses-count">{{ count }}</span>
            </div>
        </div>

        <div
            v-if="!isAuthenticated"
            class="c-timer__notice"
            :class="{ 'c-timer__notice--blocking': hasPendingPress }"
        >
            <p class="c-timer__notice-text" v-if="!hasPendingPress">
                <strong>Playing as a guest.</strong> Sign up to save this
                press to your profile &mdash; anonymous presses can't be
                recovered later.
            </p>
            <p class="c-timer__notice-text" v-else>
                <strong>You've already pressed as a guest.</strong> Sign up
                now to claim your press and keep playing.
            </p>
            <a href="/accounts/signup/" class="c-timer__notice-link">
                Sign up{{ hasPendingPress ? ' to claim your press' : '' }}
            </a>
        </div>

        <button
            @click="sendPress"
            class="c-timer__button c-button"
            :disabled="!canPress"
            :aria-disabled="!canPress"
        >
            {{ canPress ? 'The button' : 'Sign up to press again' }}
        </button>

        <span v-if="message" class="c-timer__message">
            {{ message }}
        </span>

        <Modal
            ref="pressedModal"
            id="vue-pressed-modal"
            title="You pressed the button!"
        >
            <div class="c-modal__result">
                <div class="c-modal__result-item">
                    Tier Achieved:
                    <span class="c-modal__result-value">{{
                        lastPress.tier_label
                    }}</span>
                </div>
                <div class="c-modal__result-item">
                    Time Elapsed:
                    <span class="c-modal__result-value">{{
                        formatDuration(lastPress.elapsed_seconds)
                    }}</span>
                </div>
            </div>

            <div class="c-modal__share">
                <ShareButtons :url="shareUrl" :text="shareText" />
            </div>

            <div v-if="!lastPress.owner" class="c-modal__claim">
                <p class="c-modal__claim-title">Don't lose this press!</p>
                <p class="c-modal__claim-text">
                    You're not signed in &mdash; this result won't be saved
                    to a profile unless you sign up right now.
                </p>
                <a href="/accounts/signup/" class="c-modal__claim-link c-button">
                    Sign up and claim this press
                </a>
            </div>

            <button
                type="button"
                class="c-modal__actions c-button"
                data-a11y-dialog-hide
                aria-label="Close dialog"
            >
                Close
            </button>
        </Modal>
    </div>
</template>
